import Typography from "@mui/material/Typography";
import {
  Grid,
  Dialog,
  DialogContent,
  Slide,
  Stack,
  Box,
  Chip,
  IconButton,
  Button,
  LinearProgress,
} from "@mui/material";
import { forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setProductsOrdersDetailsDialog } from "../../../../../app/slices/productsOrdersSlice";
import dayjs from "dayjs";
import { IoClose } from "react-icons/io5";
import {
  HiOutlineClipboardDocumentList,
  HiOutlineCube,
  HiOutlineBanknotes,
  HiOutlineChartBarSquare,
  HiOutlineDocumentText,
} from "react-icons/hi2";

// Brand palette (Dejlah primary blue)
const BLUE = "#0C86B6";
const BLUE_DARK = "#0a6b92";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// A titled, bordered group that visually separates each part of the details.
const SectionCard = ({ icon, title, children }) => (
  <Box
    sx={{
      bgcolor: "#ffffff",
      border: "1px solid #edf0f3",
      borderRadius: 3,
      p: { xs: 2, sm: 2.5 },
      boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "12px",
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(12,134,182,0.12)",
          color: BLUE,
          fontSize: 22,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#1f2a37" }}>
        {title}
      </Typography>
    </Stack>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

// A single read-only value: caption label above the value, boxed for scanning.
const InfoField = ({ label, value, caption, color, xs = 12, sm = 6, md = 4 }) => (
  <Grid item xs={xs} sm={sm} md={md}>
    <Box
      sx={{
        border: "1px solid #edf0f3",
        borderRadius: 2,
        px: 2,
        py: 1.25,
        bgcolor: "#fbfcfd",
        height: "100%",
      }}
    >
      <Typography sx={{ color: "#667085", fontSize: 12, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 15,
          color: color ?? "#1f2a37",
          wordBreak: "break-word",
        }}
      >
        {value !== null && value !== undefined && value !== "" ? value : "-"}
      </Typography>
      {caption ? (
        <Typography sx={{ color: "#98a2b3", fontSize: 12, mt: 0.25 }}>
          {caption}
        </Typography>
      ) : null}
    </Box>
  </Grid>
);

const ProductsOrdersDetails = () => {
  const dispatch = useDispatch();
  const _ProductsOrderDetails = useSelector(
    (state) => state.productsOrders.productsOrdersDetails
  );

  const data = _ProductsOrderDetails.data ?? {};

  const handleDialog = () => {
    dispatch(setProductsOrdersDetailsDialog({}));
  };

  const statusColor =
    data?.status === "تم التسديد"
      ? "success"
      : data?.status === "رفض" || data?.status === "الغاء طلب منصة"
      ? "error"
      : data?.status === "اقساط يومية"
      ? "info"
      : "warning";

  const amount = (value) =>
    value !== null && value !== undefined
      ? `${Number(value)?.toLocaleString()} IQD`
      : "-";

  // Share of the order value already collected, used for the progress bar.
  const paidPercent =
    data?.total_amount > 0
      ? Math.min(
          100,
          Math.round(((data?.total_payments ?? 0) / data.total_amount) * 100)
        )
      : 0;

  return (
    <Dialog
      open={_ProductsOrderDetails.dialog}
      TransitionComponent={Transition}
      keepMounted
      fullWidth
      maxWidth="md"
      dir="rtl"
      onClose={handleDialog}
      aria-describedby="alert-dialog-slide-description"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "94vh",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BLUE} 0%, #13a2d4 100%)`,
          px: { xs: 2.5, sm: 3 },
          py: 2.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: "14px",
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(255,255,255,0.18)",
              color: "#fff",
              fontSize: 26,
              flexShrink: 0,
            }}
          >
            <HiOutlineClipboardDocumentList />
          </Box>
          <Box>
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: 20,
                lineHeight: 1.2,
              }}
            >
              تفاصيل الطلب
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.82)", fontSize: 13, mt: 0.25 }}
            >
              {`رقم الطلب #${data?.id ?? "-"} • ${data?.pos_name ?? "-"}`}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            label={data?.status}
            color={statusColor}
            sx={{ fontWeight: 700, bgcolor: "#fff" }}
            variant="outlined"
          />
          <IconButton
            onClick={handleDialog}
            sx={{
              color: "#fff",
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            <IoClose />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ bgcolor: "#f4f6f8", p: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2.5}>
          {/* Payment progress */}
          <Box
            sx={{
              bgcolor: "#ffffff",
              border: "1px solid #edf0f3",
              borderRadius: 3,
              p: { xs: 2, sm: 2.5 },
              boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1.25 }}
            >
              <Typography sx={{ fontWeight: 700, color: "#1f2a37" }}>
                نسبة التسديد
              </Typography>
              <Typography sx={{ fontWeight: 700, color: BLUE }}>
                {paidPercent}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={paidPercent}
              sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: "#eef1f4",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 5,
                  backgroundColor: BLUE,
                },
              }}
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 1.25 }}
            >
              <Typography sx={{ fontSize: 13, color: "#12805c" }}>
                المسددة: {amount(data?.total_payments)}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#b42318" }}>
                المتبقي: {amount(data?.remaining)}
              </Typography>
            </Stack>
          </Box>

          {/* Order info */}
          <SectionCard icon={<HiOutlineCube />} title="معلومات الطلب">
            <InfoField label="اسم المنتج" value={data?.product_name} />
            <InfoField label="العدد" value={data?.count} />
            <InfoField label="اسم المجهز" value={data?.supplier_name} />
            <InfoField
              label="نقطة البيع"
              value={data?.pos_name}
              caption={data?.pos_number}
            />
            <InfoField
              label="الصفحة الثانوية"
              value={data?.product_sub_category_name}
            />
            <InfoField
              label="تاريخ الطلب"
              value={
                data?.created_at
                  ? dayjs(data.created_at).format("YYYY-MM-DD / HH:mm")
                  : "-"
              }
            />
            <InfoField
              label="تاريخ التحديث"
              value={
                data?.updated_at
                  ? dayjs(data.updated_at).format("YYYY-MM-DD / HH:mm")
                  : "-"
              }
            />
            {data?.product_logo ? (
              <Grid item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    border: "1px solid #edf0f3",
                    borderRadius: 2,
                    p: 1.25,
                    bgcolor: "#fbfcfd",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={data.product_logo}
                    alt={data?.product_name}
                    sx={{
                      width: 110,
                      maxHeight: 90,
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Grid>
            ) : null}
          </SectionCard>

          {/* Amounts */}
          <SectionCard icon={<HiOutlineBanknotes />} title="المبالغ">
            <InfoField
              label="سعر المنتج الكلي"
              value={amount(data?.total_amount)}
            />
            <InfoField
              label="مبلغ التسديد اليومي"
              value={amount(data?.daily_payment)}
            />
            <InfoField
              label="المبالغ المسددة"
              value={amount(data?.total_payments)}
              color="#12805c"
            />
            <InfoField
              label="المبالغ المتبقية"
              value={amount(data?.remaining)}
              color="#b42318"
            />
            <InfoField label="نسبة الخصم" value={amount(data?.discount)} />
          </SectionCard>

          {/* Pricing & revenue */}
          <SectionCard
            icon={<HiOutlineChartBarSquare />}
            title="الاسعار والارباح"
          >
            <InfoField label="سعر الشراء" value={amount(data?.buying_price)} />
            <InfoField label="سعر البيع" value={amount(data?.selling_price)} />
            <InfoField
              label="نسبة الشركة"
              value={amount(data?.revenue)}
              color={BLUE}
            />
          </SectionCard>

          {/* Notes & description */}
          <SectionCard icon={<HiOutlineDocumentText />} title="ملاحظات ووصف">
            <InfoField
              label="ملاحظات"
              value={data?.note}
              md={12}
              sm={12}
              xs={12}
            />
            <InfoField
              label="وصف المنتج"
              value={data?.product_description}
              md={12}
              sm={12}
              xs={12}
            />
          </SectionCard>
        </Stack>
      </DialogContent>

      {/* Footer */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          bgcolor: "#ffffff",
          borderTop: "1px solid #edf0f3",
          display: "flex",
          justifyContent: "flex-end",
          flexShrink: 0,
        }}
      >
        <Button
          onClick={handleDialog}
          variant="contained"
          sx={{
            bgcolor: BLUE,
            px: 4,
            boxShadow: "0 4px 12px rgba(12,134,182,0.3)",
            "&:hover": { bgcolor: BLUE_DARK },
          }}
        >
          إغلاق
        </Button>
      </Box>
    </Dialog>
  );
};

export default ProductsOrdersDetails;
